import { useContext, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  EllipsisVerticalIcon,
  Menu,
  useMenu,
  useToaster
} from "@dealer-center/core-components";
import { Locale } from "@dealer-center/ddc-core-utils";
import { useResetPasswordMutation } from "@dealer-center/ddc-pages-configuration-fetch";
import {
  ResetPasswordModal,
  SCREEN_READER_ONLY,
  UserInfoStatusContext,
  collapsedActionsOnly,
  labelledActionsOnly
} from "../../components/UserInfo";
import { resetErrorMessage } from "../writeErrorMessages";
import { DeleteUserModal } from "./DeleteUserModal";

export interface HeaderActionsProps {
  /** Whether a user is selected. Reset Password needs a subject to act on. */
  isSubjectSelected: boolean;
  /**
   * Whether the selected user **is** the operator. Forwarded to the reset dialog, where it selects
   * the unavailability notice's copy register — "your password" only when it really is.
   */
  isSubjectTheOperator?: boolean;
  /**
   * Whether the User Information form differs from the stored record — i.e. whether Save has
   * anything to do.
   *
   * Passed in rather than computed here, because dirtiness is a comparison between
   * `useUserInfoEdits`' overrides and the fetched record, and this component owns neither. The shell
   * already derives it for `usePendingEditsGuard`, so this reads the same value that decides whether
   * navigating away prompts — Save and the prompt can never disagree about whether there are edits.
   *
   * Defaults to `false`, so a caller that forgets it gets an inactive Save rather than one that
   * claims there is something to save.
   */
  isDirty?: boolean;
  isSaving?: boolean;
  /** Resolves `true` once saved; `false` when nothing was sent, validation blocked it, or it failed. */
  onSave?: () => Promise<boolean>;
  subjectUserId?: string;
  accountId?: string;
  subjectDisplayName?: string;
  isDeleting?: boolean;
  /** Resolves `undefined` once the user is gone and the selection cleared, else the failure copy. */
  onDelete?: () => Promise<string | undefined>;
}

/**
 * FR-020's **exactly three** actions — Reset Password, Delete, Save — positioned outside and above
 * the tab set so they are visible from every tab.
 *
 * **A separate component from My Info's, not a parameterized shared one** (plan Complexity
 * Tracking). Three actions vs two, FR-063's self-delete rule, and a different FR-057
 * disabled-reason treatment. One component driven by flags would render a different action set per
 * caller — more coupling than the ~60 lines it saves — and would drag User Manager's FR-057
 * obligation into My Info's shipped behavior as an untested side-effect.
 *
 * ## What is enabled and what is not
 *
 * | Action | State | Why |
 * |---|---|---|
 * | **Save** | **ENABLED when the form is dirty** | saves the profile |
 * | **Delete** | **ENABLED** unless the subject is the operator | opens a confirmation |
 * | **Reset Password** | **ENABLED** when a subject is selected | resets the password |
 *
 * Save tracks dirty state, reversing an earlier rule:
 *
 * > Save renders unconditionally inactive — NOT `disabled={!isDirty}`. FR-083 is explicit: with no
 * > endpoint, "a Save that lights up on a dirty form is precisely the
 * > control-that-masquerades-as-working" that FR-057 forbids.
 *
 * Closing the reset dialog unmounts it, discarding what was typed. On success this closes it and
 * announces; on failure it stays open with `errorMessage`.
 *
 * **`isSubjectSelected === false` is not reachable from the composed page, and that is a property of
 * the page's layout rather than of this component.** `UserManager` mounts this row only inside its
 * `subject ? (…)` branch; its no-subject arm replaces the whole detail panel with the
 * `UM_NO_SELECTION` message, which says the same thing this control's hidden reason says and says it
 * *visibly*. So the disabled arm is asserted by `HeaderActions.spec.tsx` alone and by no
 * composition test — **do not read a passing suite as evidence the page renders a disabled Reset
 * Password anywhere.** The prop stays because FR-057 attaches a reason to each control's own
 * unavailable state, and the write stories may yet mount this row above the empty panel; if they
 * settle on keeping the header hidden with no subject, the arm and `UM_RESET_PASSWORD_NO_SELECTION`
 * are then dead copy to remove together.
 *
 * ## The disabled reasons are screen-reader-only (FR-057, SC-014)
 *
 * **Do NOT print them beside the controls** — reviewer decision 2026-09-08; across four disabled
 * controls it read as clutter. FR-057 still holds: `disabled` conveys unavailability visually *and*
 * programmatically, and the adjacent hidden region carries the *why*.
 *
 * **The cost, recorded so it is not rediscovered as a defect:** a hidden adjacent region is reached
 * by navigating **past** the control, not read **with** it. The direct mechanism does not exist —
 * Interstate's `ButtonProps` exposes **no `aria-describedby`**, and `aria-label` would *replace* the
 * button's accessible name with a sentence. Adding `aria-describedby` to the wrapper is a
 * `libs/core/components` API change benefiting every page, so it is a separate, independently
 * reviewable change and a known follow-up — not re-derived work.
 *
 * **Do not use a block `Alert` in this flex cluster.** It forces the cluster to the alert's width
 * and wraps the buttons — that happened on My Info. The pressure to reach for an `Alert` will recur.
 *
 * Linked users and the default admin are handled reactively: upstream's 403 comes back as an error
 * in the dialog. Do not derive linked status from `useGetBridgeId`, `platformId` or a `userId` pattern.
 *
 * The shell owns the delete (`onDelete`) because success clears the selection, unmounting this row.
 * It also clears the live region on selection change (`SubjectStatusBridge`), so that delete and create
 * can announce after the change instead of being wiped by it.
 */
export const HeaderActions = ({
  isSubjectSelected,
  isSubjectTheOperator = false,
  isDirty = false,
  isSaving = false,
  onSave,
  subjectUserId,
  accountId,
  subjectDisplayName,
  isDeleting = false,
  onDelete
}: HeaderActionsProps) => {
  const [isResetOpen, setIsResetOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | undefined>();
  const isDeleteInFlightRef = useRef(false);
  const isDeleteAvailable =
    isSubjectSelected && !isSubjectTheOperator && Boolean(onDelete);
  const [resetError, setResetError] = useState<string | undefined>();
  const menuReferenceProps = useMenu();
  const resetPassword = useResetPasswordMutation();
  const { toastError, toastSuccess } = useToaster();
  const announce = useContext(UserInfoStatusContext);
  // A ref, not `isPending`: set synchronously, so a second confirm cannot slip in before re-render.
  const isResetInFlightRef = useRef(false);
  // Cancel stays live mid-reset, so an outcome can land after the selection changed: it is then only
  // announced and toasted, never shown in the new subject's dialog.
  const subjectUserIdRef = useRef(subjectUserId);

  useEffect(() => {
    subjectUserIdRef.current = subjectUserId;
  }, [subjectUserId]);

  // Writes clear the region when they start, so a repeated identical success is still spoken.
  const announceSuccess = (key: string) => {
    const text = Locale.getText(key) as string;
    announce(text);
    toastSuccess(text, "");
  };

  const handleResetOpen = () => {
    setResetError(undefined);
    setIsResetOpen(true);
  };

  /*
   * Named `handle*` per the repo's `react/jsx-handler-names` rule. Interstate hands `onSelect` a
   * `MenuItemEvent`, which this ignores — the menu item's identity is already fixed by which option
   * carries the handler.
   */
  const handleResetPasswordSelected = () => {
    handleResetOpen();
  };

  const handleResetClose = () => {
    setIsResetOpen(false);
    setResetError(undefined);
  };

  // `password` goes only to the mutation: never into state, logs or analytics.
  const handleResetConfirm = (password: string) => {
    if (isResetInFlightRef.current || !subjectUserId || !accountId) {
      return;
    }

    const resetUserId = subjectUserId;
    isResetInFlightRef.current = true;
    setResetError(undefined);
    announce("");
    resetPassword.mutate(
      { userId: resetUserId, accountId, password },
      {
        onSuccess: () => {
          if (subjectUserIdRef.current === resetUserId) {
            setIsResetOpen(false);
          }
          announceSuccess("UM_PW_RESET_SUCCESS");
        },
        onError: error => {
          if (subjectUserIdRef.current === resetUserId) {
            setResetError(resetErrorMessage(error));
            return;
          }
          const text = resetErrorMessage(error);
          announce(text);
          toastError(text);
        },
        onSettled: () => {
          isResetInFlightRef.current = false;
          // Lets `gcTime: 0` drop the mutation's `variables`, which hold the password.
          resetPassword.reset();
        }
      }
    );
  };

  const handleDeleteOpen = () => {
    setDeleteError(undefined);
    setIsDeleteOpen(true);
  };

  const handleDeleteSelected = () => {
    handleDeleteOpen();
  };

  const handleDeleteClose = () => {
    setIsDeleteOpen(false);
    setDeleteError(undefined);
  };

  // On success the shell clears the selection and unmounts this row; a failure keeps the dialog open.
  const handleDeleteConfirm = async () => {
    if (isDeleteInFlightRef.current || !onDelete) {
      return;
    }

    isDeleteInFlightRef.current = true;
    setDeleteError(undefined);
    announce("");
    try {
      const failure = await onDelete();
      if (failure) {
        setDeleteError(failure);
      }
    } finally {
      isDeleteInFlightRef.current = false;
    }
  };

  const handleSave = async () => {
    if (!onSave) {
      return;
    }

    announce("");
    if (await onSave()) {
      announceSuccess("UM_SAVE_SUCCESS");
    }
  };

  /*
   * A named wrapper rather than `onClick={menuReferenceProps.onClick}`, which `react/jsx-handler-
   * names` rejects. `useMenu` needs the click event to position the menu against the trigger, so it
   * is forwarded rather than swallowed.
   */
  const handleOtherActionsClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    menuReferenceProps.onClick?.(event);
  };

  return (
    <Box
      data-testid={"usermanager-header-actions-content"}
      sx={{
        alignItems: "center",
        columnGap: "8px",
        display: "flex",
        flexWrap: "wrap",
        rowGap: "8px"
      }}
    >
      <Box sx={{ position: "relative", ...labelledActionsOnly("block") }}>
        <Button
          analyticsConfiguration={{
            eventName: "usermanager-reset-password",
            eventProperties: undefined
          }}
          buttonStyle={"tertiary"}
          data-testid={"usermanager-reset-password"}
          disabled={!isSubjectSelected}
          onClick={handleResetOpen}
        >
          {Locale.getText("RESET_PASSWORD") as string}
        </Button>

        {/* Only a reason when it IS disabled — an enabled control needs no explanation. */}
        {!isSubjectSelected && (
          <Box
            data-testid={"usermanager-reset-password-reason"}
            sx={SCREEN_READER_ONLY}
          >
            {Locale.getText("UM_RESET_PASSWORD_NO_SELECTION") as string}
          </Box>
        )}
      </Box>

      <Box sx={{ position: "relative", ...labelledActionsOnly("block") }}>
        <Button
          analyticsConfiguration={{
            eventName: "usermanager-delete-user",
            eventProperties: undefined
          }}
          buttonStyle={"tertiary"}
          data-testid={"usermanager-delete"}
          disabled={!isDeleteAvailable}
          onClick={handleDeleteOpen}
        >
          {Locale.getText("DELETE_USER") as string}
        </Button>

        {isSubjectTheOperator ? (
          <Box
            data-testid={"usermanager-delete-reason"}
            sx={SCREEN_READER_ONLY}
          >
            {Locale.getText("UM_DEL_SELF") as string}
          </Box>
        ) : null}
      </Box>

      {/*
       * ## The mobile overflow menu — the two secondary actions only
       *
       * Reset Password and Delete collapse here below `md`; **Save does not** (see the next block).
       * TA4236901's reasoning for moving Delete in particular is the point: a destructive action
       * next to a back affordance at thumb height is asking for accidents.
       *
       * The French labels settle it independently — `RESET_PASSWORD` is
       * "réinitialiser le mot de passe" (29 chars) and `DELETE_USER` is "Supprimer l'utilisateur"
       * (23). Neither can be a visible button at 390px; `SAVE` at "Enregistrer" (11) can.
       *
       * `OTHER_ACTIONS` names the trigger rather than `MENU`, which is already the word the navbar's
       * collapsed nav control puts on screen — two icon affordances both reading "Menu" on the same
       * narrow viewport is a real ambiguity, and "Menu" implies navigation where this holds actions.
       */}
      <Box sx={{ position: "relative", ...collapsedActionsOnly("block") }}>
        {/*
         * `Button` + `Menu` + `useMenu` rather than `core-components`' own `MenuButton`, for one
         * concrete reason: `MenuButton`'s `ButtonProps` is Interstate's **raw** `ButtonProps`, which
         * has no `analyticsConfiguration`, so routing the trigger through it would silently drop the
         * GTM event that every other control on this page emits. Composing the three pieces keeps
         * the repo's analytics `Button` — the same composition `MenuButton` performs internally, and
         * the same one `core/navbar`'s `accountSelector` uses.
         */}
        <Button
          analyticsConfiguration={{
            eventName: "usermanager-other-actions",
            eventProperties: undefined
          }}
          aria-haspopup={"true"}
          aria-label={Locale.getText("OTHER_ACTIONS") as string}
          buttonStyle={"tertiary"}
          data-testid={"usermanager-other-actions"}
          icon={<EllipsisVerticalIcon title={""} />}
          size={"lg"}
          onClick={handleOtherActionsClick}
        />

        <Menu
          menuOptions={[
            {
              disabled: !isSubjectSelected,
              id: "usermanager-menu-reset-password",
              label: Locale.getText("RESET_PASSWORD") as string,
              onSelect: handleResetPasswordSelected,
              value: "reset-password"
            },
            {
              disabled: !isDeleteAvailable,
              id: "usermanager-menu-delete",
              label: Locale.getText("DELETE_USER") as string,
              onSelect: handleDeleteSelected,
              value: "delete"
            }
          ]}
          referenceProps={menuReferenceProps.referenceProps}
        />

        {/*
         * ⚠️ **FR-057's reasons cannot ride on the menu items, so they ride here.**
         *
         * Interstate's `MenuOption` is `{ label?, disabled?, divider?, id?, value?, onSelect?,
         * icon? }` and **`label` is a plain `string`, not `ReactNode`** — there is no slot for a
         * nested element and no `aria-describedby`. So the per-control `SCREEN_READER_ONLY` sibling
         * the desktop buttons above use has nowhere to attach inside the menu.
         *
         * This block is the compromise: one region beside the trigger carrying the reason for every
         * disabled item. The association is looser than a per-control sibling — a screen reader
         * hears it with the trigger rather than with the item — but it is announced, it is not
         * printed, and it honours both FR-057 and the 2026-09-08 reviewer decision that the reasons
         * stay screen-reader-only.
         *
         * **Do NOT fold the reason into the `label` string.** That prints it beside the control,
         * which is precisely what that decision rules out (across four disabled controls it read as
         * clutter). Flagged to the reviewer as the one place the mobile layout weakens an existing
         * guarantee rather than preserving it.
         */}
        {isSubjectTheOperator ? (
          <Box
            data-testid={"usermanager-other-actions-reasons"}
            sx={SCREEN_READER_ONLY}
          >
            {Locale.getText("UM_DEL_SELF") as string}
          </Box>
        ) : null}
      </Box>

      {/*
       * ## Save stays a visible button at every width
       *
       * It does not collapse into the menu, for two reasons:
       *
       * 1. **Desktop already ships a visible disabled Save.** Hiding it on mobile would make the two
       *    layouts disagree about what the page offers, which is worse than a quiet inactive
       *    control.
       * 2. **Save is the view's primary action.** Overflow menus are for secondary and destructive
       *    actions; burying the primary one inverts that. And leaving it in place means the write
       *    story enables a button rather than re-laying-out the header.
       *
       * The "dead UI" objection that motivated hiding it was really an objection to a **sticky
       * footer** — a permanent 56px band on the narrowest screen in the product. It does not
       * transfer to a ~74px button in a row that exists anyway.
       */}
      <Box sx={{ position: "relative" }}>
        <Button
          analyticsConfiguration={{
            eventName: "usermanager-save",
            eventProperties: undefined
          }}
          buttonStyle={"primary"}
          data-testid={"usermanager-save"}
          disabled={!isDirty || isSaving}
          onClick={handleSave}
        >
          {Locale.getText("SAVE") as string}
        </Button>

        {/*
         * No screen-reader reason here, unlike Delete above. Save is inactive because the form
         * matches the stored record — the ordinary meaning of a disabled Save, which needs no
         * explanation. `UM_SAVE_UNAVAILABLE` ("Saving changes is not available yet") was removed
         * with this change rather than kept: it would be a false statement about a button that
         * works.
         */}
      </Box>

      <ResetPasswordModal
        isWriteAvailable
        errorMessage={resetError}
        isOpen={isResetOpen}
        isSaving={resetPassword.isPending}
        isSubjectTheOperator={isSubjectTheOperator}
        onClose={handleResetClose}
        onConfirm={handleResetConfirm}
      />

      <DeleteUserModal
        errorMessage={deleteError}
        isDeleting={isDeleting}
        isOpen={isDeleteOpen}
        subjectDisplayName={subjectDisplayName ?? subjectUserId ?? ""}
        subjectUserId={subjectUserId ?? ""}
        onClose={handleDeleteClose}
        onConfirm={handleDeleteConfirm}
      />
    </Box>
  );
};
